import { codeExample, defineDemoElement, demoLayoutStyles, stripIndent } from './demo-shared.js';
import { css, html, LitElement } from 'lit';
import { query, state } from 'lit/decorators.js';
import type { DemoSection } from './demo-shared.js';
import type { WebmoduleRequestCloseEvent } from '../../events/webmodule-request-close.js';
import type WebmoduleButton from '../../components/button/button.js';
import type WebmoduleDrawer from '../../components/drawer/drawer.js';
import type WebmoduleModal from '../../components/modal/modal.js';

const FILLER_BODY =
  'This body is placeholder content - in a real screen it would hold a form, a summary or any other markup. ' +
  'The overlay sizes itself to its content, and the body scrolls when the content does not fit.';

interface DemoModalOptions {
  size?: 'small' | 'medium' | 'large';
  noHeader?: boolean;
  headerActions?: boolean;
  preventClose?: boolean;
  tallContent?: boolean;
}

/** One reusable modal, reconfigured per scenario button. */
class DemoModal extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }
    `
  ];

  @query('webmodule-modal') private modal: WebmoduleModal;
  @state() private size: 'small' | 'medium' | 'large' = 'medium';
  @state() private noHeader = false;
  @state() private headerActions = false;
  @state() private preventClose = false;
  @state() private tallContent = false;

  render() {
    return html`
      <h4>Size</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ size: 'small' })}>Small</webmodule-button>
        <webmodule-button @click=${() => this.open({ size: 'medium' })}>Medium</webmodule-button>
        <webmodule-button @click=${() => this.open({ size: 'large' })}>Large</webmodule-button>
      </div>

      <h4>Header</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ noHeader: true })}>No header</webmodule-button>
        <webmodule-button @click=${() => this.open({ headerActions: true })}>Header actions</webmodule-button>
      </div>

      <h4>Behavior</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open({ preventClose: true })}>
          Prevent close (button only)
        </webmodule-button>
        <webmodule-button @click=${() => this.open({ tallContent: true })}>Scrolling body</webmodule-button>
      </div>

      <webmodule-modal
        label="Modal example"
        size=${this.size}
        ?no-header=${this.noHeader}
        @webmodule-request-close=${(e: WebmoduleRequestCloseEvent) => {
          if (this.preventClose && e.detail.source !== 'close-button') {
            e.preventDefault();
          }
        }}
      >
        ${this.headerActions
          ? html`<webmodule-icon-button slot="header-actions" name="eye" label="Preview"></webmodule-icon-button>`
          : ''}
        <div style=${this.tallContent ? 'height: 150vh;' : ''}>
          ${FILLER_BODY}
          ${this.preventClose
            ? html`<p><strong>Overlay and Escape are vetoed - only the close button dismisses this modal.</strong></p>`
            : ''}
          ${this.noHeader
            ? html`<webmodule-button style="margin-top: 1rem;" @click=${() => this.modal.hide()}
                >Close</webmodule-button
              >`
            : ''}
        </div>
        <webmodule-button slot="footer" variant="primary" @click=${() => this.modal.hide()}>Close</webmodule-button>
      </webmodule-modal>
    `;
  }

  private open(options: DemoModalOptions) {
    this.size = options.size ?? 'medium';
    this.noHeader = options.noHeader ?? false;
    this.headerActions = options.headerActions ?? false;
    this.preventClose = options.preventClose ?? false;
    this.tallContent = options.tallContent ?? false;

    void this.updateComplete.then(() => this.modal.show());
  }
}

/** One reusable drawer with placement/containment scenarios. */
class DemoDrawer extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }

      .stage {
        position: relative;
        height: 260px;
        border: dashed 2px var(--webmodule-color-neutral-300);
        border-radius: var(--webmodule-border-radius-medium);
        padding: var(--webmodule-spacing-medium);
        margin-bottom: var(--webmodule-spacing-large);
        color: var(--webmodule-color-neutral-500);
        overflow: hidden;
      }
    `
  ];

  @query('webmodule-drawer') private drawer: WebmoduleDrawer;
  @state() private placement: 'top' | 'end' | 'bottom' | 'start' = 'end';
  @state() private contained = false;

  render() {
    return html`
      <div class="stage">
        A position: relative container - the contained drawer renders inside it.
        <webmodule-drawer label="Drawer example" placement=${this.placement} ?contained=${this.contained}>
          ${FILLER_BODY}
          <webmodule-button slot="footer" variant="primary" @click=${() => this.drawer.hide()}>Close</webmodule-button>
        </webmodule-drawer>
      </div>

      <h4>Containment</h4>
      <div class="demo-row">
        <webmodule-button @click=${() => this.open('end', false)}>Viewport</webmodule-button>
        <webmodule-button @click=${() => this.open('end', true)}>Contained</webmodule-button>
      </div>

      <h4>Placement</h4>
      <div class="demo-row" @click=${(e: PointerEvent) => this.openFromValue(e)}>
        <webmodule-button value="top">Top</webmodule-button>
        <webmodule-button value="end">End</webmodule-button>
        <webmodule-button value="bottom">Bottom</webmodule-button>
        <webmodule-button value="start">Start</webmodule-button>
      </div>
    `;
  }

  private openFromValue(e: PointerEvent) {
    const value = (e.target as WebmoduleButton).value as 'top' | 'end' | 'bottom' | 'start' | undefined;

    if (value) {
      this.open(value, this.contained);
    }
  }

  private open(placement: 'top' | 'end' | 'bottom' | 'start', contained: boolean) {
    this.placement = placement;
    this.contained = contained;

    void this.updateComplete.then(() => this.drawer.show());
  }
}

/** Interactive playground for the low-level popup positioning primitive. */
class DemoPopup extends LitElement {
  static styles = [
    demoLayoutStyles,
    css`
      :host {
        display: block;
      }

      .stage {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        border: dashed 2px var(--webmodule-color-neutral-300);
        border-radius: var(--webmodule-border-radius-medium);
        margin-bottom: var(--webmodule-spacing-large);
      }

      .anchor {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 9rem;
        height: 3rem;
        border-radius: var(--webmodule-border-radius-medium);
        background: var(--webmodule-color-neutral-100);
        border: solid 1px var(--webmodule-color-neutral-300);
      }

      .bubble {
        padding: var(--webmodule-spacing-small) var(--webmodule-spacing-medium);
        background: var(--webmodule-color-primary-600);
        color: var(--webmodule-color-neutral-0);
        border-radius: var(--webmodule-border-radius-medium);
        font-size: var(--webmodule-font-size-small);
      }
    `
  ];

  @state() private active = true;
  @state() private placement = 'top';
  @state() private arrow = true;
  @state() private distance = 8;

  render() {
    return html`
      <div class="stage">
        <webmodule-popup
          placement=${this.placement}
          ?active=${this.active}
          ?arrow=${this.arrow}
          distance=${this.distance}
        >
          <span slot="anchor" class="anchor">Anchor</span>
          <div class="bubble">Positioned content</div>
        </webmodule-popup>
      </div>

      <div class="demo-row">
        <webmodule-toggle
          ?checked=${this.active}
          @webmodule-change=${(e: Event) =>
            (this.active = (e.target as HTMLInputElement & { checked: boolean }).checked)}
        >
          Active
        </webmodule-toggle>
        <webmodule-toggle
          ?checked=${this.arrow}
          @webmodule-change=${(e: Event) =>
            (this.arrow = (e.target as HTMLInputElement & { checked: boolean }).checked)}
        >
          Arrow
        </webmodule-toggle>
        <webmodule-select
          label="Placement"
          class="webmodule-control-left-label"
          size="small"
          style="min-width: 240px;"
          .values=${['top', 'top-start', 'top-end', 'right', 'bottom', 'bottom-start', 'bottom-end', 'left'].map(p => ({
            value: p,
            text: p
          }))}
          .value=${this.placement}
          @webmodule-change=${(e: Event) => (this.placement = (e.target as HTMLInputElement).value)}
        ></webmodule-select>
        <webmodule-input
          label="Distance"
          class="webmodule-control-left-label"
          size="small"
          type="number"
          style="width: 160px;"
          .value=${String(this.distance)}
          @webmodule-input=${(e: Event) => (this.distance = Number((e.target as HTMLInputElement).value) || 0)}
        ></webmodule-input>
      </div>
    `;
  }
}

export function registerOverlayDemos() {
  defineDemoElement('demo-modal', DemoModal);
  defineDemoElement('demo-drawer', DemoDrawer);
  defineDemoElement('demo-popup', DemoPopup);
}

export const modalSection: DemoSection = {
  id: 'modal',
  title: 'Modal',
  tags: ['webmodule-modal'],
  summary:
    'A dialog rendered above a page overlay. Closing is negotiable: listen for webmodule-request-close and call ' +
    'preventDefault() to veto specific dismissal sources.',
  render: () => html`
    <demo-example
      label="Scenarios"
      .code=${stripIndent(`
        <webmodule-modal label="Modal example" size="medium">
          Body content...
          <webmodule-icon-button slot="header-actions" name="eye" label="Preview"></webmodule-icon-button>
          <webmodule-button slot="footer" variant="primary">Close</webmodule-button>
        </webmodule-modal>

        // Veto overlay/escape dismissal:
        modal.addEventListener('webmodule-request-close', e => {
          if (e.detail.source !== 'close-button') e.preventDefault();
        });`)}
    >
      <demo-modal></demo-modal>
    </demo-example>
  `
};

export const drawerSection: DemoSection = {
  id: 'drawer',
  title: 'Drawer',
  tags: ['webmodule-drawer'],
  summary:
    'A panel that slides in from an edge of the viewport - or of its own parent when contained is set and the ' +
    'parent has position: relative.',
  render: () => html`
    <demo-example
      label="Scenarios"
      .code=${stripIndent(`
        <webmodule-drawer label="Drawer example" placement="end" contained>
          Body content...
          <webmodule-button slot="footer" variant="primary">Close</webmodule-button>
        </webmodule-drawer>`)}
    >
      <demo-drawer></demo-drawer>
    </demo-example>
  `
};

export const popupSection: DemoSection = {
  id: 'popup',
  title: 'Popup',
  tags: ['webmodule-popup'],
  summary:
    'The low-level positioning primitive behind dropdown, tooltip, select and combobox. Use it directly when ' +
    'building custom floating UI; it only positions - showing, hiding and dismissal are yours to implement.',
  render: () => html`
    <demo-example
      label="Playground"
      .code=${stripIndent(`
        <webmodule-popup placement="top" active arrow distance="8">
          <span slot="anchor">Anchor</span>
          <div>Positioned content</div>
        </webmodule-popup>`)}
    >
      <demo-popup></demo-popup>
    </demo-example>
    ${codeExample({
      label: 'Notes',
      description: html`Popup is intentionally minimal: combine <code>flip</code>, <code>shift</code> and
        <code>auto-size</code> to keep content in the viewport, and <code>hoist</code>-style fixed positioning via
        <code>strategy="fixed"</code> when clipped by overflow containers.`,
      code: `
        <!-- set active to position the content -->
        <webmodule-popup placement="bottom-start" flip shift strategy="fixed" distance="4">
          <span slot="anchor">...</span>
          <div>...</div>
        </webmodule-popup>`
    })}
  `
};
